import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import AdBanner from '../components/AdBanner';
import { generateInvoicePDF, generateBlankInvoicePDF, downloadPDF } from '../utils/pdfGenerator';
import api from '../services/api';
import './DocumentForm.css';

const Invoice = () => {
  const location = useLocation();
  const editDocument = location.state?.editDocument;
  const { isAuthenticated, isPremium } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    from: {
      name: '',
      businessName: '',
      address: { street: '', city: '', state: '', zipCode: '' },
      phone: '',
      email: ''
    },
    to: {
      name: '',
      businessName: '',
      address: { street: '', city: '', state: '', zipCode: '' },
      phone: '',
      email: ''
    },
    items: [
      { description: '', quantity: 1, unitPrice: 0, amount: 0 }
    ],
    taxRate: 0,
    discount: 0,
    discountType: 'fixed',
    paymentTerms: 'due-on-receipt',
    paymentMethod: 'cash',
    paymentInstructions: '',
    issueDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    notes: '',
    platform: 'other'
  });

  // Pre-fill form if editing an existing document
  useEffect(() => {
    if (editDocument) {
      setIsEditing(true);
      setFormData({
        from: {
          name: editDocument.from?.name || '',
          businessName: editDocument.from?.businessName || '',
          address: {
            street: editDocument.from?.address?.street || '',
            city: editDocument.from?.address?.city || '',
            state: editDocument.from?.address?.state || '',
            zipCode: editDocument.from?.address?.zipCode || ''
          },
          phone: editDocument.from?.phone || '',
          email: editDocument.from?.email || ''
        },
        to: {
          name: editDocument.to?.name || '',
          businessName: editDocument.to?.businessName || '',
          address: {
            street: editDocument.to?.address?.street || '',
            city: editDocument.to?.address?.city || '',
            state: editDocument.to?.address?.state || '',
            zipCode: editDocument.to?.address?.zipCode || ''
          },
          phone: editDocument.to?.phone || '',
          email: editDocument.to?.email || ''
        },
        items: editDocument.items?.length > 0 
          ? editDocument.items.map(item => ({
              description: item.description || '',
              quantity: item.quantity || 1,
              unitPrice: item.unitPrice || 0,
              amount: item.amount || 0
            }))
          : [{ description: '', quantity: 1, unitPrice: 0, amount: 0 }],
        taxRate: editDocument.taxRate || 0,
        discount: editDocument.discount || 0,
        discountType: editDocument.discountType || 'fixed',
        paymentTerms: editDocument.paymentTerms || 'due-on-receipt',
        paymentMethod: editDocument.paymentMethod || 'cash',
        paymentInstructions: editDocument.paymentInstructions || '',
        issueDate: editDocument.issueDate 
          ? new Date(editDocument.issueDate).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0],
        dueDate: editDocument.dueDate 
          ? new Date(editDocument.dueDate).toISOString().split('T')[0]
          : '',
        notes: editDocument.notes || '',
        platform: editDocument.platform || 'other'
      });
      
      toast.info('Editing saved invoice - changes will create a new version');
    }
  }, [editDocument]);

  const handleChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: typeof prev[section] === 'object' && !Array.isArray(prev[section])
        ? { ...prev[section], [field]: value }
        : value
    }));
  };

  const handleAddressChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        address: {
          ...prev[section].address,
          [field]: value
        }
      }
    }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      
      // Calculate amount
      if (field === 'quantity' || field === 'unitPrice') {
        const qty = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(newItems[index].quantity) || 0;
        const price = field === 'unitPrice' ? parseFloat(value) || 0 : parseFloat(newItems[index].unitPrice) || 0;
        newItems[index].amount = qty * price;
      }
      
      return { ...prev, items: newItems };
    });
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, unitPrice: 0, amount: 0 }]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => 
      sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0), 0
    );
    const taxAmount = subtotal * ((parseFloat(formData.taxRate) || 0) / 100);
    const discountAmount = formData.discountType === 'percentage' 
      ? subtotal * ((parseFloat(formData.discount) || 0) / 100)
      : (parseFloat(formData.discount) || 0);
    const total = subtotal + taxAmount - discountAmount;

    return { subtotal, taxAmount, discountAmount, total };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { subtotal, taxAmount, total } = calculateTotals();
      
      const invoiceData = {
        ...formData,
        subtotal,
        taxAmount,
        total,
        items: formData.items.map(item => ({
          ...item,
          quantity: parseFloat(item.quantity) || 0,
          unitPrice: parseFloat(item.unitPrice) || 0,
          amount: (parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0)
        }))
      };

      // If authenticated, save to server
      if (isAuthenticated) {
        const response = await api.post('/invoices', invoiceData);
        invoiceData.invoiceNumber = response.data.invoice.invoiceNumber;
        
        if (response.data.saved) {
          toast.success('Invoice saved to your account!');
        } else if (isAuthenticated) {
          toast.info('Upgrade to Premium to save documents', { autoClose: 4000 });
        }
      } else {
        // Generate temporary invoice number
        const timestamp = Date.now().toString(36).toUpperCase();
        const random = Math.random().toString(36).substring(2, 6).toUpperCase();
        invoiceData.invoiceNumber = `INV-${timestamp}-${random}`;
      }

      // Generate and download PDF
      const pdf = generateInvoicePDF(invoiceData);
      downloadPDF(pdf, `invoice-${invoiceData.invoiceNumber}.pdf`);
      
      toast.success('PDF downloaded successfully!');

      if (!isAuthenticated) {
        toast.info('Sign up to save your documents!', { autoClose: 5000 });
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
      toast.error('Error generating document. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const { subtotal, taxAmount, discountAmount, total } = calculateTotals();

  const handleDownloadBlankTemplate = () => {
    const pdf = generateBlankInvoicePDF();
    downloadPDF(pdf, 'invoice-template.pdf');
    toast.success('Blank template downloaded!');
  };

  return (
    <div className="document-page">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">
            {isEditing ? 'Edit Invoice' : 'Invoice Generator'}
          </h1>
          <p className="page-subtitle">
            {isEditing 
              ? 'Make changes and generate a new PDF'
              : 'Create a professional invoice for your transaction'
            }
          </p>
        </div>

        {/* Blank Template CTA - only for unauthenticated users */}
        {!isAuthenticated && !isEditing && (
          <div className="blank-template-card">
            <div className="blank-template-content">
              <div className="blank-template-icon">📄</div>
              <div className="blank-template-text">
                <h3>Free Printable Invoice Template</h3>
                <p>Download a blank invoice template (PDF) to print and fill out by hand</p>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-outline blank-template-btn"
              onClick={handleDownloadBlankTemplate}
            >
              Download Blank Invoice
            </button>
          </div>
        )}

        <AdBanner slot="invoice-top" />

        <form onSubmit={handleSubmit} className="document-form">
          {/* From (Sender) Information */}
          <div className="form-section">
            <h2 className="section-title">From (Your Information)</h2>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Your Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.from.name}
                  onChange={(e) => handleChange('from', 'name', e.target.value)}
                  placeholder="John Smith"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Business Name (Optional)</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.from.businessName}
                  onChange={(e) => handleChange('from', 'businessName', e.target.value)}
                  placeholder="Your Business LLC"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={formData.from.email}
                  onChange={(e) => handleChange('from', 'email', e.target.value)}
                  placeholder="you@example.com"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  className="form-input"
                  value={formData.from.phone}
                  onChange={(e) => handleChange('from', 'phone', e.target.value)}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Street Address</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.from.address.street}
                  onChange={(e) => handleAddressChange('from', 'street', e.target.value)}
                  placeholder="123 Main St"
                />
              </div>
              <div className="form-group">
                <label className="form-label">City</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.from.address.city}
                  onChange={(e) => handleAddressChange('from', 'city', e.target.value)}
                  placeholder="New York"
                />
              </div>
              <div className="form-group form-group-half">
                <label className="form-label">State</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.from.address.state}
                  onChange={(e) => handleAddressChange('from', 'state', e.target.value)}
                  placeholder="NY"
                />
              </div>
              <div className="form-group form-group-half">
                <label className="form-label">ZIP Code</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.from.address.zipCode}
                  onChange={(e) => handleAddressChange('from', 'zipCode', e.target.value)}
                  placeholder="10001"
                />
              </div>
            </div>
          </div>

          {/* To (Recipient) Information */}
          <div className="form-section">
            <h2 className="section-title">Bill To (Customer Information)</h2>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Customer Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.to.name}
                  onChange={(e) => handleChange('to', 'name', e.target.value)}
                  placeholder="Jane Doe"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Business Name (Optional)</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.to.businessName}
                  onChange={(e) => handleChange('to', 'businessName', e.target.value)}
                  placeholder="Customer Business"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={formData.to.email}
                  onChange={(e) => handleChange('to', 'email', e.target.value)}
                  placeholder="customer@example.com"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Street Address</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.to.address.street}
                  onChange={(e) => handleAddressChange('to', 'street', e.target.value)}
                  placeholder="456 Oak Ave"
                />
              </div>
              <div className="form-group">
                <label className="form-label">City</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.to.address.city}
                  onChange={(e) => handleAddressChange('to', 'city', e.target.value)}
                  placeholder="Los Angeles"
                />
              </div>
              <div className="form-group form-group-half">
                <label className="form-label">State</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.to.address.state}
                  onChange={(e) => handleAddressChange('to', 'state', e.target.value)}
                  placeholder="CA"
                />
              </div>
              <div className="form-group form-group-half">
                <label className="form-label">ZIP Code</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.to.address.zipCode}
                  onChange={(e) => handleAddressChange('to', 'zipCode', e.target.value)}
                  placeholder="90001"
                />
              </div>
            </div>
          </div>

          {/* Invoice Items */}
          <div className="form-section">
            <h2 className="section-title">Invoice Items</h2>
            <table className="items-table">
              <thead>
                <tr>
                  <th className="col-description">Description</th>
                  <th className="col-quantity">Qty</th>
                  <th className="col-price">Price</th>
                  <th className="col-amount">Amount</th>
                  <th className="col-actions"></th>
                </tr>
              </thead>
              <tbody>
                {formData.items.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        type="text"
                        className="form-input"
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        placeholder="Item description"
                        required
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="form-input"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                        min="1"
                        step="1"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="form-input"
                        value={item.unitPrice}
                        onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                      />
                    </td>
                    <td>
                      <span className="item-amount">
                        ${((parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0)).toFixed(2)}
                      </span>
                    </td>
                    <td>
                      {formData.items.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => removeItem(index)}
                        >
                          ✕
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              type="button"
              className="btn btn-secondary add-item-btn"
              onClick={addItem}
            >
              + Add Item
            </button>

            {/* Totals */}
            <div className="totals-section">
              <div className="totals-table">
                <div className="totals-row">
                  <span>Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="totals-row">
                  <span>Tax ({formData.taxRate}%)</span>
                  <span>${taxAmount.toFixed(2)}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="totals-row">
                    <span>Discount</span>
                    <span>-${discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="totals-row total">
                  <span>Total</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Invoice Settings */}
          <div className="form-section">
            <h2 className="section-title">Invoice Settings</h2>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Issue Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.issueDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, issueDate: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Due Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.dueDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Payment Terms</label>
                <select
                  className="form-select"
                  value={formData.paymentTerms}
                  onChange={(e) => setFormData(prev => ({ ...prev, paymentTerms: e.target.value }))}
                >
                  <option value="due-on-receipt">Due on Receipt</option>
                  <option value="net-15">Net 15</option>
                  <option value="net-30">Net 30</option>
                  <option value="net-60">Net 60</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Tax Rate (%)</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.taxRate}
                  onChange={(e) => setFormData(prev => ({ ...prev, taxRate: e.target.value }))}
                  min="0"
                  max="100"
                  step="0.01"
                  placeholder="0"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Discount</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.discount}
                  onChange={(e) => setFormData(prev => ({ ...prev, discount: e.target.value }))}
                  min="0"
                  step="0.01"
                  placeholder="0"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Discount Type</label>
                <select
                  className="form-select"
                  value={formData.discountType}
                  onChange={(e) => setFormData(prev => ({ ...prev, discountType: e.target.value }))}
                >
                  <option value="fixed">Fixed Amount ($)</option>
                  <option value="percentage">Percentage (%)</option>
                </select>
              </div>
              <div className="form-group form-group-full">
                <label className="form-label">Payment Instructions</label>
                <textarea
                  className="form-textarea"
                  value={formData.paymentInstructions}
                  onChange={(e) => setFormData(prev => ({ ...prev, paymentInstructions: e.target.value }))}
                  placeholder="e.g., PayPal: your@email.com, Venmo: @yourhandle, or bank details..."
                  rows={2}
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="form-section">
            <h2 className="section-title">Notes</h2>
            <div className="form-group">
              <textarea
                className="form-textarea"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Thank you for your business!"
                rows={3}
              />
            </div>
          </div>

          {/* Submit */}
          <div className="form-actions-bottom">
            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={loading}
            >
              {loading ? 'Generating...' : 'Generate & Download PDF'}
            </button>
            {!isAuthenticated && (
              <p className="form-note">
                💡 <a href="/register">Sign up</a> to save your invoices
              </p>
            )}
            {isAuthenticated && !isPremium() && (
              <p className="form-note">
                ⭐ <a href="/subscription">Upgrade to Premium</a> to access your invoice history
              </p>
            )}
          </div>
        </form>

        <AdBanner slot="invoice-bottom" />
      </div>
    </div>
  );
};

export default Invoice;
